/**
 * お金まわり（Money: 売上・出金・受け取りの登録・前払い残高・利用料・パスキー）の画面の英語の文言。Functions・X の共有は en.fnShare.ts（2026-09-28 に移した）。
 * en.ts が `...enMoney` で取り込む（キーの正は en。ja.money.ts が同じキーを全部持つことを型で強制）。
 *
 * WHY 別ファイルか: en.ts は 550 行を超えていて、お金の画面だけで 250 キー近くある。お金の言い回し（Kumo の言葉で言う・
 * Stripe の名前を出さない）をまとめて見直せるようにする。
 *
 * 言い回しの決め事（MASTER-PLAN「クリエイターは Stripe を意識しない」「AI ネイティブ運用＝見るときは分かりやすく」）:
 *   - Stripe / Connect / acct_ / Account Session などの名前を出さない。「受け取りの登録」「本人確認」「銀行口座」と言う
 *   - 数字には「それは何で・いつ動くか」を 1 行添える（保留 30 日・手数料は誰が取るか）
 *   - エラーは「次に何をすればいいか」で終える
 */
export const enMoney = {
  // ------------------------------------------------------------ shell
  'money.topbarLink': 'Money',
  // 2026-10-07 追記 7（管理の判断）: 「10% consumption tax」とは書かない（利用者に税を課しているように読める・国も匂う）→ 決まった 1 文を率に依存せずいつも出す。
  //   税は請求書に税が載る事業者の項目だけ（今は Cloudflare）。月末は戻すだけ（追記 2/3）。サイトの料金ページ・CLI・MCP と同じ文（言い回しを足さない）
  'money.pricesFromProviders': 'Prices are at cost, including the tax our providers add.',
  'money.refundOnly': 'Usage is taken from your balance as it happens. At the end of the month, part of it may come back if it fell within an included monthly amount; nothing is ever taken afterwards.',
  'money.navLabel': 'Money',
  'money.account': 'Your account',
  'money.accountSub': 'Earnings & billing',
  // 売上（payouts）を閉じている間のサイドバーの見出し（2026-09-30 console/words-pass・MoneyShell.tsx EARN_HIDDEN）
  'money.accountSubLaunch': 'Billing',
  'money.nav.group.earn': 'Earnings',
  'money.nav.group.spend': 'Usage & billing',
  'money.nav.group.security': 'Security',
  'money.nav.earnings': 'Earnings',
  'money.nav.withdraw': 'Withdraw',
  'money.nav.getPaid': 'Payout setup',
  'money.nav.prepaid': 'Prepaid balance',
  'money.nav.usage': 'Usage',
  'money.nav.passkeys': 'Passkeys',
  'money.mode.legend': 'Mode',
  'money.mode.live': 'Live',
  'money.mode.test': 'Test',
  'money.band.testTag': 'TEST',
  'money.band.test': 'Test mode: earnings from your development environments. No real money moves.',
  // 2026-09-28: 実際の動き（MASTER-PLAN §2・servingGate.ts）に合わせた = 請求残の間は利用者向けの処理（API・配信・マルチプレイ・Functions・AI）が止まる。
  //   以前は「Functions だけ止まる・公開中はそのまま」と書いていて、実際の停止と食い違っていた（money/neutral-words の指摘）
  'money.owedBanner': 'You owe {owed} of usage. Your apps are paused for their users until you top up, and they resume on their own once you do.',
  'money.owedBannerLink': 'Top up',

  // ------------------------------------------------------------ earnings
  'earn.title': 'Earnings',
  'earn.lead':
    'Each sale is held for 30 days in case of refunds or chargebacks, then becomes available to withdraw. {product} takes 0%; only the card processing fee is deducted.',
  'earn.pending': 'On hold',
  'earn.reserve': 'Reserve',
  'earn.pendingHint': 'available 30 days after each sale',
  'earn.available': 'Available',
  'earn.availableHint': 'ready to withdraw',
  'earn.availableHintSetup': 'withdraw after payout setup',
  'earn.owedHint': 'below zero: refunds after a payout. Your next sales cover it',
  'earn.inTransit': 'On the way',
  'earn.inTransitHint': 'being sent to your bank',
  'earn.paid': 'Paid out',
  'earn.paidHint': 'all time',
  'earn.withdraw': 'Withdraw',
  'earn.setUp': 'Set up payouts',
  'earn.notReady': "You can't withdraw yet: {reason}",
  'earn.bySource': 'By source',
  'earn.byGame': 'By project',
  'earn.source.sale': 'In-app purchases',
  'earn.source.ad': 'Ads',
  'earn.source.issuer_reward': 'Referral rewards',
  'earn.source.pass': 'Pass',
  // 自分の支払い（自分のゲームで自分が払った分・売上に含めない・0252）。判定の条件（カード・氏名の一致）は細かく書かない
  'earn.source.self_purchase': 'Your own payments (not counted as earnings)',
  'earn.self.tile': 'Your own payments',
  'earn.self.tileHint': "not counted as earnings · can't be withdrawn",
  'earn.self.title': 'Your own payments (not counted as earnings)',
  'earn.self.body':
    'Purchases paid with your card or in your name are not counted as earnings. After 30 days we move them to your prepaid balance (usable for usage fees, not withdrawable). If this is a mistake, contact support.',
  'earn.self.byGame': '+ {amount} your own payments (not counted)',
  'earn.self.col.when': 'When',
  'earn.self.col.game': 'Project',
  'earn.self.col.paid': 'Paid',
  'earn.self.col.amount': 'Your share',
  'earn.self.col.status': 'Status',
  'earn.self.status.excluded': 'On hold',
  'earn.self.status.prepaid': 'Moved to prepaid',
  'earn.self.status.released': 'Released by support',
  'earn.self.none': 'None yet.',
  'earn.col.source': 'Source',
  'earn.col.count': 'Count',
  'earn.col.gross': 'Gross',
  'earn.col.reversed': 'Refunded',
  'earn.col.net': 'Net',
  'earn.col.game': 'Project',
  'earn.col.sales': 'Sales',
  'earn.unknownGame': 'Deleted project',
  'earn.emptyTitle': 'No earnings yet',
  'earn.emptyBody': 'When users buy something in your apps, or you turn on ads, earnings show up here.',
  'earn.emptyBodyTest': 'Test-mode earnings come from purchases in your development environments.',
  'earn.payouts': 'Payouts',
  'earn.noPayouts': 'No payouts yet.',
  'earn.col.requested': 'Requested',
  'earn.col.amount': 'Amount',
  'earn.col.fee': 'Fee',
  'earn.col.status': 'Status',
  'earn.col.via': 'From',
  'earn.via.dashboard': 'Dashboard',
  'earn.via.mcp': 'AI agent',
  'earn.instant': 'instant',
  'earn.statement': 'Download statement (CSV)',
  'earn.statementHint': 'Every balance change in the last 12 months',
  'payout.status.draft': 'draft',
  'payout.status.approved': 'approved',
  'payout.status.transferring': 'sending',
  'payout.status.paid': 'paid',
  'payout.status.failed': 'failed',
  'payout.status.reversed': 'returned',
  'payout.status.cancelled': 'cancelled',

  // ------------------------------------------------------------ payout setup
  'payee.title': 'Payout setup',
  'payee.lead':
    'Tell us where to send your earnings. Identity verification and bank details are handled securely on this page. You never need a separate payments account.',
  'payee.headline.not_started': 'Not set up yet',
  'payee.headline.needs_info': 'More information needed',
  'payee.headline.in_review': 'Checking your details',
  'payee.headline.ready': 'Ready to receive payouts',
  'payee.headline.on_hold': 'Payouts are on hold',
  'payee.headline.closed': 'Payout account closed',
  'payee.body.not_started': 'Pick your country to start. It takes about 5 minutes: your legal name, date of birth, tax ID and a bank account or debit card.',
  'payee.body.needs_info': 'We need a few more details to verify your identity. Continue where you left off.',
  'payee.body.in_review': "You're done for now. Checks usually finish within minutes, sometimes a day or two. We'll email you if anything else is needed.",
  'payee.body.ready': 'You can withdraw available earnings any day, in any amount.',
  'payee.body.on_hold': 'Our team paused payouts on this account. Your earnings are safe. Check your email or contact support.',
  'payee.body.closed': 'This payout account was closed. Contact support to set up a new one.',
  'payee.dueCount': { one: '{count} item still needed', other: '{count} items still needed' },
  'payee.pastDue': 'Overdue: payouts are paused until you finish.',
  'payee.country': 'Country',
  'payee.country.US': 'United States',
  'payee.country.CA': 'Canada',
  'payee.countryHint': 'Payouts are available to creators in the US and Canada.',
  'payee.countryLocked': 'Country: {country}',
  'payee.start': 'Start setup',
  'payee.continue': 'Continue setup',
  'payee.update': 'Review details',
  'payee.refresh': 'Check status',
  'payee.checked': 'Status updated.',
  'payee.tax': 'Tax form',
  'payee.tax.w9': 'W-9',
  'payee.tax.w8ben': 'W-8BEN',
  'payee.taxStatus.missing': 'not provided',
  'payee.taxStatus.pending': 'checking',
  'payee.taxStatus.verified': 'verified',
  'payee.taxStatus.invalid': 'needs a fix',
  'payee.status': 'Status',
  'payee.sim.title': 'Simulated verification',
  'payee.sim.body':
    'This server has no payment keys, so no real identity check runs and no money moves. On a real server, the secure verification form appears here.',
  'payee.sim.finish': 'Finish simulated setup',
  'payee.embed.loading': 'Loading the secure form…',
  'payee.embed.close': 'Close form',
  'payee.embed.failed': "The verification form didn't load. Check your connection and try again.",
  'payee.embed.unavailableTitle': "Payout setup isn't available on this server yet.",
  'payee.embed.unavailableBody': 'The server needs {keyVar}. Ask whoever runs your {product} server to set it.',
  'payee.testNote': 'Test mode: use test details. Nothing here is a real account.',

  // ------------------------------------------------------------ withdraw
  'wd.title': 'Withdraw',
  'wd.lead': 'Withdraw any part of your available balance, any day. The payout fee is taken from the amount you withdraw.',
  'wd.available': 'Available to withdraw',
  'wd.amount': 'Amount',
  'wd.all': 'All',
  'wd.speed': 'Speed',
  'wd.standard': 'Standard',
  'wd.standardHint': 'usually 2 business days',
  'wd.instant': 'Instant',
  'wd.instantHint': 'within minutes to an eligible debit card, extra fee',
  'wd.youReceive': 'You receive',
  'wd.fee': 'Payout fee',
  'wd.fee.payout': 'Transfer',
  'wd.fee.instant': 'Instant',
  'wd.fee.crossBorder': 'Currency conversion',
  'wd.fee.account': 'Monthly account fee (first payout this month)',
  'wd.feeNote': 'Fees are what the payment network charges. {product} adds nothing.',
  'wd.quoting': 'Calculating…',
  'wd.continue': 'Continue',
  'wd.continueHint': "Next, you'll confirm with your passkey. Nothing is sent before that.",
  'wd.invalid': 'Enter an amount like 25 or 25.50.',
  'wd.tooMuch': 'That is more than your available balance ({amount}).',
  'wd.belowFee': "That amount doesn't cover the payout fee.",
  'wd.notReadyTitle': "You can't withdraw yet",
  'wd.reason.setup': 'payouts are not set up',
  'wd.reason.review': 'your details are being checked',
  'wd.reason.info': 'more information is needed',
  'wd.reason.tax': 'your tax information is being checked',
  'wd.reason.hold': 'payouts are on hold',
  'wd.reason.other': 'payouts are not enabled yet',
  'wd.goSetup': 'Go to payout setup',
  'wd.noPasskeyTitle': 'Add a passkey first',
  'wd.noPasskeyBody': 'Every withdrawal is confirmed with a passkey (Face ID, Touch ID, Windows Hello or a security key).',
  'wd.addPasskey': 'Add a passkey',
  'wd.nothingTitle': 'Nothing to withdraw yet',
  'wd.nothingBody': 'Sales become available 30 days after purchase.',
  'wd.mcpNote': 'You can also ask your AI agent to withdraw (via MCP). You still confirm each one here with your passkey.',

  // ------------------------------------------------------------ confirm withdrawal（MCP からもここに来る）
  'cf.eyebrow': 'Withdrawal',
  'cf.title': 'Confirm withdrawal',
  'cf.lead': 'Check the amount, then confirm with your passkey. Nothing is sent until you confirm.',
  'cf.fromMcp': 'Your AI agent (MCP) asked for this withdrawal. Only confirm if you asked for it.',
  'cf.amount': 'From your balance',
  'cf.fee': 'Payout fee',
  'cf.net': 'You receive',
  'cf.speed': 'Speed',
  'cf.expires': 'Link expires',
  'cf.mode': 'Mode',
  'cf.confirm': 'Confirm with passkey',
  'cf.testConfirm': 'Confirm without a passkey (test)',
  'cf.testNote': 'Test mode on a non-production server: you can confirm without a passkey. Live withdrawals always need one.',
  'cf.cancel': 'Cancel request',
  'cf.cancelled': 'Request cancelled. No money moved.',
  'cf.sentTitle': 'Withdrawal sent',
  'cf.sentBody': '{net} is on its way to your bank.',
  'cf.queuedTitle': 'Withdrawal approved',
  'cf.queuedBody': "The transfer didn't go through on the first try. We keep retrying and email you if it fails.",
  'cf.failedTitle': 'The withdrawal failed',
  'cf.failedBody': 'The transfer did not go through. The amount is back in your available balance.',
  'cf.status.executed': 'This withdrawal was already confirmed.',
  'cf.status.confirmed': 'This withdrawal is confirmed and being processed.',
  'cf.status.expired': 'This link expired. Start a new withdrawal.',
  'cf.status.cancelled': 'This request was cancelled.',
  'cf.status.rejected': 'This request was declined.',
  'cf.noPasskey': 'Add a passkey to confirm withdrawals.',
  'cf.notFoundTitle': 'Withdrawal not found',
  'cf.notFoundBody': 'The link is wrong, or it belongs to another {product} account. Log in with the account that asked for it.',
  'cf.toEarnings': 'Go to earnings',
  'cf.newWithdrawal': 'New withdrawal',
  'cf.passkeyClosed': 'The passkey prompt was closed. Nothing was sent.',

  // ------------------------------------------------------------ passkeys
  'pk.title': 'Passkeys',
  'pk.lead':
    'Passkeys confirm withdrawals, from this dashboard and from AI agents (MCP). A new passkey can confirm live withdrawals 72 hours after you add it.',
  'pk.add': 'Add a passkey',
  'pk.addTitle': 'Add a passkey',
  'pk.label': 'Name',
  'pk.labelPlaceholder': 'e.g. MacBook Touch ID',
  'pk.passwordHint': 'First you confirm it is you (your password, or signing in again with Google or another linked account), so someone at your unlocked computer cannot add their own.',
  'pk.addConfirm': 'Continue',
  'pk.added': 'Passkey added. It can confirm live withdrawals from {when}.',
  'pk.unsupported': "This browser can't use passkeys. Try a recent Chrome, Safari, Edge or Firefox.",
  'pk.emptyTitle': 'No passkeys yet',
  'pk.emptyBody': 'Add one before your first withdrawal.',
  'pk.col.name': 'Name',
  'pk.col.added': 'Added',
  'pk.col.lastUsed': 'Last used',
  'pk.col.live': 'Live withdrawals',
  'pk.liveReady': 'ready',
  'pk.liveFrom': 'from {when}',
  'pk.remove': 'Remove',
  'pk.removeTitle': 'Remove “{label}”?',
  'pk.removeBody': "You won't be able to confirm withdrawals with it anymore.",
  'pk.removed': 'Passkey removed.',
  'pk.cancelled': 'The passkey prompt was closed.',

  // ------------------------------------------------------------ prepaid
  'pp.title': 'Prepaid balance',
  // 2026-09-28 founder「抽象的な言い回しはやめていい」: スローガン調の短い文をやめ、何が起きるかを書く
  'pp.lead': '{product} takes the cost of what your projects use from this balance, at the price {product} pays its providers. It is charged from the first request; there is no free allowance, and any invite credit is used first. You can add funds at any time.',
  // 2026-10-18 design/rauno-dashboard: 前払い残高の上の 1 行から、下の「料金のしくみ」の段（pp.lead・手数料の説明・例）へのリンクと、その段の見出し
  'pp.howPricing': 'How pricing works',
  'pp.balance': 'Balance',
  'pp.balanceHint': 'alert below {line}',
  'pp.balanceHintOff': 'alerts off',
  // 招待クレジットの間、サーバーが効かせる線（lowLineCents）が設定より低いとき（moneyLogic.ts balanceLineHint・Money 2026-09-30 の文そのまま）
  'pp.balanceHintInvite': "You'll be notified below {line} while you're on invite credit (your setting: {setting}).",
  'pp.owed': 'Owed',
  'pp.owedHint': 'usage beyond your balance',
  'pp.runway': 'Lasts about',
  'pp.runwayDays': { one: '{count} day', other: '{count} days' },
  'pp.runwayHint': "at this month's pace",
  'pp.runwayIdle': 'No usage yet',
  'pp.runwayIdleHint': 'nothing used this month',
  'pp.runwayZero': 'Ran out',
  'pp.expiringSoon': '{amount} expires within 30 days',
  'pp.owedTitle': 'Your balance ran out',
  'pp.owedBody': 'You owe {owed} of usage. Until you top up, your apps are paused for their users (published apps, servers, multiplayer, AI), and you cannot create or publish projects. Everything resumes on its own once you top up. Turning on automatic top-up prevents this.',
  'pp.lowTitle': 'Your balance is running low',
  'pp.lowBodyDays': 'About {days} days left at your current usage. Top up, or turn on automatic top-up.',
  'pp.lowBody': 'Top up, or turn on automatic top-up, so nothing pauses.',
  // 招待クレジット・使い切り（2026-09-29 creator/launch-close-2・Money の前払いの道 20。文はサーバーの知らせのメール lowBalance.ts に合わせた）
  'pp.lowTitleInvite': 'Your invite credit is almost used up',
  'pp.lowBodyInvite': 'To keep going, add prepaid funds.',
  'pp.outTitle': 'Your prepaid balance is used up',
  'pp.outTitleInvite': 'Your credit is used up (including your invite credit)',
  'pp.outBody': 'Until you add funds, your apps are paused for their users (published apps, servers, multiplayer, AI), and you cannot create or publish projects. Everything resumes on its own once you add funds.',
  'pp.topUp': 'Add funds',
  'pp.amount': 'Amount',
  'pp.other': 'Other',
  'pp.otherAmount': 'Other amount',
  // 2026-10-07 追記 8（founder「B がいいよ」）: 入金の手数料は上乗せ = 選んだ額がきっちり残高に入り、カードにはその額 + 手数料。額はサーバーの見積もり（topup-quote）
  'pp.feeNote': 'The card processing fee (about 6%) is added on top, so the amount you choose is what lands in your balance. If the actual fee is lower, the difference goes to your balance; nothing more is taken if it is higher.',
  'pp.range': 'From {min} to {max}.',
  // 2026-10-07 console/usage-view（founder「そのことはクリエイターに伝えておきたい」）: 残高に入る額・払う額・手数料を数字で並べる（以前は割合の 1 文 pp.feeShare）。同日 founder の決定で手数料は上乗せ = 「Add X → you pay Y」の向き
  'pp.feeLine': 'Add {added} to your balance → you pay {pay} (card fee about {fee})',
  // 2026-10-07 追記 9 A（管理）: 実際の手数料は 2.9% + 30¢ ではない = 見積もりは 6.2%・固定なし（0300）。率は「約 6%」とだけ（国・換算・理由は書かない）。固定の部分が無いので「少額ほど割合が大きい」は言わない
  'pp.feeExamples': 'Examples:',
  'pp.feeExample': 'Add {added} → you pay {pay} (fee about {fee})',
  'pp.autoFee': 'Each automatic top-up: add {added} → you pay {pay} (card fee about {fee})',
  'pp.saveCard': 'Save this card for automatic top-up',
  'pp.pay': 'Pay {amount}',
  'pp.paying': 'Opening checkout…',
  'pp.topUpDone': '{amount} was added to your prepaid balance.',
  // 入金は手動キャプチャ = 戻った時点では決まっていない。completed になるまで「確認しています」（PrepaidPage・moneyDisplay.ts topUpOutcome）
  'pp.topUpPending': 'Checking your payment…',
  'pp.topUpStillChecking': "We're still checking your payment. Your balance updates once it's confirmed.",
  // 3D セキュアで断った入金（card_not_protected）。理由（3D セキュア・不正対策）は書かない（founder 判断 2026-09-27）
  'pp.topUpCardRefused': "This card can't be used to add funds. Please try a different card.",
  'pp.topUpCanceled': 'Top-up canceled. You were not charged.',
  'pp.chargedFee': 'Your card was charged {charge} (card fee {fee}).',
  'pp.topUpFailed': "The top-up didn't complete. You were not charged.",
  'pp.settings': 'Alerts and automatic top-up',
  'pp.lowLine': 'Alert me when the balance drops below',
  'pp.lowLineHint': 'By email, and to the AI agent you connected. 0 turns alerts off.',
  'pp.auto': 'Automatic top-up',
  'pp.autoWhy': 'Your balance is prepaid and never goes negative: when it runs out, your apps pause for their users. Automatic top-up is the way to keep them running without interruption. Any amount from the top-up minimum to your top-up maximum.',
  'pp.autoOn': 'On',
  'pp.autoOff': 'Off',
  'pp.autoBelow': 'When the balance drops below',
  'pp.autoAmount': 'Add',
  // 2026-10-07 追記 8: 自動チャージも手数料は上乗せ
  'pp.autoCard': 'Charged to {card}, plus the card fee (the amount you set lands in your balance).',
  'pp.autoNoCard': 'Add funds once with “Save this card” checked, then turn this on.',
  'pp.autoFailed': 'The last automatic top-up failed {ago}. We wait 24 hours before trying again.',
  'pp.autoFund': 'Use my earnings first',
  'pp.autoFundHint': 'When the prepaid balance is short, cover usage from your available earnings.',
  'pp.save': 'Save',
  'pp.saved': 'Settings saved.',
  'pp.history': 'History',
  'pp.noHistory': 'No activity yet.',
  'pp.col.when': 'When',
  'pp.col.what': 'What',
  'pp.col.amount': 'Amount',
  'pp.src.prepaid_topup': 'Added funds',
  'pp.src.usage_charge': 'Usage',
  'pp.src.usage_refund': 'Refund: usage within the included monthly amount',
  'pp.src.prepaid_fund': 'From earnings',
  'pp.src.prepaid_offset': 'Covered a refund',
  'pp.src.prepaid_expire': 'Expired',
  'pp.src.prepaid_refund': 'Top-up refunded',
  'pp.src.prepaid_dispute': 'Top-up disputed',
  'pp.src.prepaid_dispute_won': 'Dispute resolved',
  'pp.src.self_purchase_to_prepaid': 'From your own payments',
  'pp.src.invite_credit': 'Invite credit',
  'pp.notice.auto_topup_succeeded': 'Automatic top-up of {amount} completed {ago}.',
  'pp.notice.auto_topup_failed': 'Automatic top-up failed {ago}.',

  // ------------------------------------------------------------ usage
  // founder 2026-09-27（MASTER-PLAN §1c）: 「Kumo の利用料（原価どおり）」の英語 = 原価そのまま・上乗せなしを見出しで伝える
  'use.title': '{product} usage (at cost)',
  'use.lead': 'What your projects used and what it cost. Features you have not turned on cost nothing.',
  'use.month': 'Month',
  'use.charged': 'Charged',
  'use.chargedHint': 'taken from your prepaid balance',
  'use.metered': 'Metered cost',
  'use.meteredHint': 'before rounding up to the cent',
  'use.shared': 'Shared plan costs',
  'use.sharedHint': 'split by usage when the month closes',
  'use.adjusted': 'Refund: usage within the included monthly amount',
  'use.adjustedHint': 'returned to your balance',
  'use.status.open': 'in progress',
  'use.status.closed': 'closed',
  'use.status.none': 'no usage',
  'use.byPart': 'By part',
  'use.byDay': 'By day',
  'use.byDayNote': "Daily figures are estimates: each day's usage × that day's price.",
  'use.emptyTitle': 'No usage this month',
  'use.emptyBody': 'This shows up as soon as someone uses one of your projects (for example a sign-in, a save, a page view or a Functions request).',
  'use.refresh': 'Refresh now',
  'use.updated': 'updated {ago}',
  'use.col.part': 'Part',
  'use.col.cost': 'Cost',
  'use.col.share': 'Share',
  'use.chartLabel': 'Daily usage cost by kind, {period}',
  'use.total': 'Total',
  'use.other': 'Other',
  // Kumo の定額の機能（2026-09-28 money/slug-redirect・0262）。原価ではなく Kumo の料金 = 「原価どおり」の部品とは別の欄で見せる（founder: 任意の便利機能の小さな定額は Kumo の収入）
  'use.addons': '{product} add-ons (fixed price)',
  'use.addonsNote': 'Optional features with a small fixed price set by {product}. Not part of the at-cost usage above.',
  'use.addonsTile': 'Add-ons',
  'use.addonsTileHint': 'fixed-price features, from your prepaid balance',
  'use.addon.slug_redirect': 'Address redirect ($1/month)',
  'use.addon.slug_redirectDetail': '{count} redirect-days this month · $1 per old address for a full month, counted by the day',
  'use.addonsRefunded': '{amount} returned after a correction',
  'use.addonsDayNote': '{product} add-ons are not in these bars.',
  // 2026-09-28: 「プレイヤー」→「利用者」/ "Users"（Creator がダッシュボード全体の用語をそろえた・MASTER-PLAN §0。キー名は変えない）
  'comp.players': 'Sign-in & users',
  // 2026-09-27 Quality SEC-HIDE-04 / MASTER-PLAN §1c: 最初の公開で隠す機能（ランキング・AI・ソーシャル・運営機能・通報）の名前を文言に残さない。
  // 隠した部品は利用が起きない（サーバーが閉じる）ので、ここは中立の名前にしておく。公開したら元の名前に戻す（git 履歴: 'Saves & leaderboards' / 'AI' / 'Social' / 'Live ops' / 'Anti-cheat'）
  'comp.gamedata': 'Saves & data',
  'comp.payments': 'Payments',
  'comp.hosting': 'Hosting',
  'comp.realtime': 'Multiplayer',
  'comp.ai': 'Other features',
  'comp.ads': 'Ads',
  'comp.social': 'Other features',
  'comp.liveops': 'Other features',
  'comp.integrity': 'User bans',
  'comp.compute': 'Functions',
  'comp.share': 'X sharing',
  'comp.mail_broadcast': 'User emails',
  // 部品名 platform（サーバーの Component）の表示。クリエイター向けに「Platform」と言わない（MASTER-PLAN §1c・2026-09-27 Creator の依頼）
  'comp.platform': 'Shared',
  // 計測項目の表示名（UsagePage.tsx meterLabel・2026-09-29 launch-close-2・Money の決定）
  'use.meter.functions_log_events': 'Logs',

  // ------------------------------------------------------------ usage view（2026-10-07 console/usage-view・founder「残高や DB・サーバー・転送料などの Usage を常に知りたい」）
  // いつの数字か（最終の更新と遅れを具体的に）。分はサーバーの updatesWithinMinutes（毎時の集計 = 60）
  'use.freshAgo': 'Updated {ago}.',
  'use.freshNever': 'Not updated yet this month.',
  'use.freshWithin': { one: 'New usage shows up within about {count} minute.', other: 'New usage shows up within about {count} minutes.' },
  'use.freshHour': 'New usage shows up within about an hour.',
  'use.thisMonth': 'This month',
  'use.monthTotal': 'Total',
  'use.totalHint': 'taken from your balance',
  'use.today': 'Today',
  'use.todayHint': 'so far today (UTC)',
  'use.returned': 'Returned at month end',
  'use.returnedHint': 'back to your balance',
  'use.returnedRow': 'Returned to your balance at month end',
  'use.balanceHint': 'Balance {amount}',
  // 残りの目安（データが少ないときも分かる言い方）
  'use.runwayHint': "{balance} left, at this month's pace",
  'use.runwayEarly': { one: '{balance} left. Only {count} day into the month, so this guess can change a lot.', other: '{balance} left. Only {count} days into the month, so this guess can change a lot.' },
  'use.runwayIdleHint': '{balance} left. Nothing used yet this month.',
  'use.byKind': 'By kind',
  'use.byKindNote': 'How much you used, and what it cost.',
  'use.byProject': 'By project',
  'use.byProjectNote': 'Shared plan costs and fixed-price add-ons are for your whole account, so they are not split by project.',
  'use.projectDeleted': 'Deleted project',
  // 種類（サーバー usageViews.ts USAGE_CATEGORIES）。具体的な物の名前で
  'use.cat.database': 'Database (saves, sign-ins, scores)',
  // 2026-10-15 creator/academy-friction: database から分けた（MCP・CLI・ダッシュボードからの管理の呼び出しなど。サーバー usageViews.ts categoryOf の WHY）
  'use.cat.platform': '{product} itself (managing projects, calls from your AI agent)',
  'use.cat.functions': 'Server functions',
  'use.cat.transfer': 'App delivery (data transfer)',
  'use.cat.storage': 'File storage',
  'use.cat.multiplayer': 'Online multiplayer',
  'use.cat.domains': 'Custom domains',
  'use.cat.email': 'Email',
  'use.cat.ai': 'AI',
  'use.cat.x_sign_in': 'Sign in with X',
  'use.cat.other': 'Other',
  // 量（usageView.ts categoryAmount）
  'use.qty.requests': { one: '{count} request', other: '{count} requests' },
  'use.qty.gbTransferred': '{value} GB sent',
  'use.qty.gbStored': '{value} GB stored on average',
  'use.qty.roomHours': 'rooms open {value} hours',
  'use.qty.playerHours': 'users connected {value} hours',
  'use.qty.emails': { one: '{count} email', other: '{count} emails' },
  'use.qty.domains': { one: '{count} domain', other: '{count} domains' },
  'use.qty.signIns': { one: '{count} sign-in', other: '{count} sign-ins' },
  // 上のバーの残高（components/BalanceChip.tsx）
  'topbar.balanceLabel': 'Balance {amount}. Open your balance.',
  'topbar.balanceLow': 'Low',
  'topbar.balanceOut': 'Ran out',
  // 招待クレジットがメールの確認待ち（usageView.ts balanceChip の pending・2026-10-14 gtm/onboarding）
  'topbar.balancePending': 'Confirm email',
  // まだお金が入っていない新しい人（usageView.ts balanceChip の start・2026-10-29 core/open-signup-polish）。「Ran out」は使い切った人だけ
  'topbar.balanceStart': 'Add balance',
  'topbar.autoTopUp': 'Auto top-up on',
  // プロジェクトの概要の「今月の使用額」（OverviewPage.tsx）
  'overview.usage': 'Usage this month',
  'overview.usageLink': 'See what it was for',

  // ------------------------------------------------------------ project nav extras
  'nav.functions': 'Functions',
  'nav.share': 'X sharing',
  'pay.payoutsNew': 'Payouts',
  'pay.payoutsNewLead':
    'Earnings from these sales go to your {product} balance. {product} takes 0%; the card processing fee is deducted. Payout setup and withdrawals live in Money, shared by all your projects.',
  'pay.openMoney': 'Open Money',

  // ------------------------------------------------------------ errors（お金まわりのコード）
  'error.passkey_required': 'Confirm this with a passkey. Add one under Passkeys if you have none.',
  'error.passkey_cooldown': 'This passkey was added less than 72 hours ago, so it cannot confirm live withdrawals yet.',
  'error.passkey_invalid': "The passkey couldn't be verified. Try again.",
  'error.passkey_challenge_expired': 'The passkey request expired. Try again.',
  'error.passkey_unknown': 'That passkey is not registered to this account.',
  'error.passkey_exists': 'This passkey is already registered.',
  'error.too_many_passkeys': 'You have the maximum number of passkeys. Remove one first.',
  'error.invalid_password': 'The password is incorrect.',
  'error.payee_not_created': 'Set up payouts first.',
  'error.payee_not_eligible': 'Payouts are not enabled for this account yet. Check Payout setup.',
  'error.payout_method_cooldown': 'Withdrawals are paused for 72 hours after changing where payouts go.',
  'error.first_withdrawal_requires_web': 'Your first withdrawal must be made from the dashboard.',
  'error.insufficient_available': 'Not enough available balance.',
  'error.amount_below_fee': "The amount doesn't cover the payout fee.",
  'error.withdrawal_expired': 'This confirmation link expired. Start a new withdrawal.',
  'error.withdrawal_not_pending': 'This withdrawal is no longer waiting for confirmation.',
  'error.unsupported_country': 'Payouts are available in the US and Canada only.',
  'error.payee_country_locked': 'The country cannot be changed after setup starts.',
  'error.no_saved_payment_method': 'Add funds once with “Save this card” checked, then turn automatic top-up on.',
  'error.invalid_amount': 'Check the amount.',
  'error.prepaid_required': 'Add prepaid balance first. Functions are billed at cost from it.',
  'error.balance_due': 'Your usage balance is overdue. Top up to continue.',
  'error.functions_unavailable': "This {product} server can't run Functions.",
  'error.functions_suspended': 'Functions are paused because your prepaid balance ran out.',
  // ------------------------------------------------------------ dashboard-money2（2026-09-27）: パスキーの確認・冷却・振込先・レポート
  'money.nav.sales': 'Sales report',
  'money.nav.threeDSecure': 'Card authentication',
  'pk.cooldownAll': 'Your passkeys can confirm live withdrawals from {when}. Test withdrawals work now.',
  'pk.confirmFirst': 'You already have a passkey, so you will be asked to use it first, then to create the new one. This stops someone who only knows your password from adding theirs.',
  'pk.removeVerify': 'Confirm it is you',
  'pk.removeWithPasskey': 'Use a passkey',
  'pk.removeWithPasskeyHint': 'Any of your passkeys, including this one.',
  // 2026-09-28 money/reauth-ui: 削除のもう一方の確認は Creator の再認証の部品（パスワードか Google 等のやり直し）
  'pk.removeWithReauth': 'Use your password or a linked sign-in',
  'pk.removeWithReauthHint': 'Your {product} password, or signing in again with Google or another linked account.',
  'pk.removeLastPasskeyOnly': 'This is your last passkey, so only a passkey can remove it. A password or a linked sign-in alone cannot.',
  'pk.removeLastUnsupported': "This is your last passkey and only a passkey can remove it. This browser can't use passkeys, so remove it from a device that can.",
  'pk.reauthResumed': 'Confirmed. Press “Continue” again to create the passkey.',
  'cf.cooldownUntil': 'Your passkeys are new, so they can confirm live withdrawals from {when}. The request expires before then; start a new withdrawal after that time.',
  'wd.passkeyCooldown': 'Your passkeys can confirm live withdrawals from {when}. A request made now would expire before then.',
  'bank.adviseShort': 'Tip: get paid to a bank account, not a debit card. Refunds after a payout can only be taken back from a bank account.',
  'bank.adviseTitle': 'Use a bank account for payouts',
  'bank.adviseBody':
    'If a refund or chargeback arrives after you withdraw, the amount is taken back from your bank account automatically. A debit card cannot be charged this way, so the amount stays as a balance you owe and is taken from your next earnings. If you added only a debit card, update your payout details and add a bank account.',
  'bank.addTitle': 'Add a bank account to get paid',
  'bank.replaceTitle': 'Add a bank account (a debit card is not enough)',
  'bank.whyBody': 'Refunds and chargebacks after a payout can only be taken back from a bank account. Update your payout details to add one.',
  'bank.action': 'Update payout details',

  'tds.title': 'Card authentication (3D Secure)',
  'tds.lead':
    '{product} asks for card authentication (3D Secure) on every payment, but whether it happens is up to the card issuer. Fraud chargebacks on authenticated payments usually move to the issuer; on the rest, the loss stays with you. This page only reports. Nothing is blocked.',
  'tds.group.total': 'Total only',
  'tds.kind': 'Payments',
  'tds.kind.all': 'All',
  'tds.kind.purchases': 'In-app purchases',
  'tds.kind.topups': 'Prepaid top-ups',
  'tds.emptyTitle': 'No payments in this period',
  'tds.emptyBody': 'Card payments in your apps and your prepaid top-ups show up here.',
  // 販売（payments）を閉じている間の空の状態（Money 了承 2026-09-30「販売を閉じている間は前払いの入金だけの文」）。ThreeDSecurePage が PURCHASES_HIDDEN で選ぶ
  'tds.emptyBodyLaunch': 'Your prepaid top-ups show up here.',
  'tds.payments': 'Payments',
  'tds.authShare': 'Authenticated',
  'tds.authShareHint': 'Share of payments where the issuer confirmed the cardholder',
  'tds.disputeRate': 'Chargeback rate',
  'tds.disputeRateHint': { one: '{count} chargeback, counted even if won', other: '{count} chargebacks, counted even if won' },
  'tds.unauthDisputeRate': 'Chargeback rate without authentication',
  'tds.unauthDisputeRateHint': 'The kind where the loss stays with you. Watch it above 0.75%.',
  'tds.byResult': 'By result',
  'tds.note':
    '“Unknown” is payments made before {product} started recording this (September 27, 2026) or where the result could not be read. Card networks look at chargeback rates around 0.75–1%; rates with fewer than 3 payments are not highlighted.',
  'tds.byGame': 'By project',
  'tds.byMonth': 'By month',
  'tds.prepaid': 'Prepaid top-ups',
  'tds.col.result': 'Result',
  'tds.col.payments': 'Payments',
  'tds.col.share': 'Share',
  'tds.col.authShare': 'Authenticated',
  'tds.col.unknown': 'Unknown',
  'tds.col.disputes': 'Chargebacks',
  'tds.col.fraud': 'Fraud / unrecognized',
  'tds.col.rate': 'Rate',
  'tds.col.unauthRate': 'Rate without auth',
  'tds.row.authenticated': 'Authenticated',
  'tds.row.notAuthenticated': 'Not authenticated',
  'tds.row.unknown': 'Unknown',
  'tds.result.attempt_acknowledged': 'Attempted (issuer not enrolled)',
  'tds.result.exempted': 'Exempted (low risk)',
  'tds.result.not_supported': 'Card does not support it',
  'tds.result.failed': 'Authentication failed',
  'tds.result.processing_error': 'Processing error',
  'tds.result.data_share_only': 'Data shared only',
  'tds.result.none': 'Not attempted (e.g. automatic top-up)',
  'tds.result.wallet': 'Apple Pay / Google Pay',
  'tds.result.not_card': 'Not a card (e.g. Link)',
  'tds.result.other': 'Other',

  'report.title': 'Sales report',
  'report.lead': 'What came in during a period, by day, month, project or source. The same numbers your AI agent gets from the sales report tool.',
  'report.range': 'Period',
  'report.lastDays': { one: 'Last {count} day', other: 'Last {count} days' },
  'report.groupBy': 'Group by',
  'report.group.day': 'Day',
  'report.group.month': 'Month',
  'report.group.project': 'Project',
  'report.group.source': 'Source',
  'report.col.period': 'Period',
  'report.col.project': 'Project',
  'report.col.source': 'Source',
  'report.col.count': 'Payments',
  'report.col.gross': 'Gross',
  'report.col.refunds': 'Refunds',
  'report.col.fees': 'Fees',
  'report.col.net': 'Net to you',
  'report.total': 'Total',
  'report.countHint': { one: '{count} payment', other: '{count} payments' },
  'report.feesHint': 'Payment processing and other costs, at cost. {product} takes 0%.',
  'report.netHint': 'Includes amounts still on hold',
  'report.emptyTitle': 'No sales in this period',
  'report.emptyBody': 'Sales from your projects show up here once users buy something.',
  'report.note':
    'Counted when it happened: a September refund of an August purchase is in September. Store purchases (App Store, Google Play) are not included because the money does not go through {product}.',
  'report.source.purchase': 'Purchases',
  'report.source.network_reward': 'Network rewards',
  'report.source.ad': 'Ads',
  'report.source.pass': '{product} Pass',
  'error.passkey_confirmation_required': 'Confirm with one of your passkeys (or, when removing one, your password or a linked sign-in) and try again.',
} satisfies Record<string, string | { one: string; other: string }>;
